/**
 * FULL-TIME OVERLAY — the final score and the ego stats (matchStats.ts).
 *
 * Styled as broadcast graphics rather than a dialog box: a kicker, a scoreline
 * built from the two kits, the verdict, the run's numbers, then the praise
 * lines. Pure DOM + injected CSS — nothing downloaded (§16), disposes clean
 * like every other overlay in ui/.
 *
 * ── Why it was rebuilt ─────────────────────────────────────────────────────
 * It predated the Footy Arena design system: 'system-ui' throughout, its own
 * hardcoded #ffd94a, and no relationship to the menu the player came from or
 * the pause overlay they had been using all match. It was the last screen in
 * the game still on the old look, and it is the one every match ends on.
 *
 * Three real defects went with the restyle:
 *
 *  1. THE KIT COLOUR WAS A FILL BEHIND WHITE TEXT. `homeColor`/`awayColor` come
 *     from deriveKit, which will happily produce a pale yellow or white kit —
 *     and white 800-weight text on it is unreadable. The kit is now a BAR
 *     beside the name. A team's colour identifies it; it does not have to sit
 *     underneath its letters to do that.
 *  2. IT WAS CLICK-ONLY. Full time is exactly when a controller player has put
 *     the mouse down, and there was no keyboard or gamepad path to Rematch at
 *     all. It shares `createNavOwner` with every other screen now.
 *  3. NO VERDICT. The board stated 2-1 and left the player to work out whether
 *     that was good. `verdictFor` derives it from the scoreline.
 *
 * ── The one assumption in here ─────────────────────────────────────────────
 * The verdict is phrased from the LOCAL PLAYER's point of view ("You win"),
 * which is only correct because `homeName`/`homeScore` are the local player at
 * every call site — game/practice.ts passes teamA, which is 'YOU' in a
 * practice match and the player's own club label in a competition. If this
 * overlay is ever mounted for a spectator or for the away side, pass the
 * verdict in rather than deriving it here.
 */

import { createNavOwner, type NavOwner } from './ui-nav.js';

export interface PostMatchData {
  homeName: string;
  awayName: string;
  homeScore: number;
  awayScore: number;
  /** CSS colours for the team blocks. */
  homeColor: string;
  awayColor: string;
  /** "FULL TIME" / "SESSION OVER" — the kicker above the strip. */
  kicker: string;
  /** Rows of [label, value] — distance, top speed, sprint time... */
  statRows: ReadonlyArray<readonly [string, string]>;
  /** pickHeadlines() output — records carry the leading star already. */
  headlines: readonly string[];
}

export interface PostMatchHandlers {
  onRematch: () => void;
  onMenu: () => void;
}

interface Verdict {
  text: string;
  tone: 'win' | 'loss' | 'draw';
}

function verdictFor(home: number, away: number): Verdict {
  if (home > away) return { text: 'You win', tone: 'win' };
  if (home < away) return { text: 'You lose', tone: 'loss' };
  return { text: 'Draw', tone: 'draw' };
}

const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => `&#${c.charCodeAt(0)};`);

export function mountPostMatch(
  root: HTMLElement,
  d: PostMatchData,
  h: PostMatchHandlers,
): { dispose(): void } {
  const style = document.createElement('style');
  style.textContent = CSS;

  const el = document.createElement('div');
  el.className = 'fgpm';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-label', `${d.kicker} — ${d.homeName} ${d.homeScore}, ${d.awayName} ${d.awayScore}`);

  const v = verdictFor(d.homeScore, d.awayScore);

  const stats = d.statRows
    .map(([label, value], i) => `
      <div class="fgpm-stat" style="--i:${i}">
        <b>${esc(value)}</b><i>${esc(label)}</i>
      </div>`)
    .join('');

  // A record line arrives from pickHeadlines already carrying a leading star;
  // that star is the only thing that distinguishes it, so it drives the class.
  const lines = d.headlines
    .map((t, i) => `
      <div class="fgpm-line${t.startsWith('★') ? ' is-record' : ''}" style="--i:${i}">
        ${esc(t)}
      </div>`)
    .join('');

  el.innerHTML = `
    <div class="fgpm-veil"></div>
    <div class="fgpm-grain"></div>
    <div class="fgpm-card">
      <p class="fgpm-kicker">${esc(d.kicker)}</p>

      <div class="fgpm-score">
        <div class="fgpm-team fgpm-team-home">
          <span class="fgpm-name">${esc(d.homeName)}</span>
          <span class="fgpm-kit" style="background:${esc(d.homeColor)}"></span>
        </div>
        <div class="fgpm-goals">
          <span>${d.homeScore}</span><em>–</em><span>${d.awayScore}</span>
        </div>
        <div class="fgpm-team fgpm-team-away">
          <span class="fgpm-kit" style="background:${esc(d.awayColor)}"></span>
          <span class="fgpm-name">${esc(d.awayName)}</span>
        </div>
      </div>

      <p class="fgpm-verdict is-${v.tone}">${v.text}</p>

      <div class="fgpm-stats">${stats}</div>
      <div class="fgpm-lines">${lines}</div>

      <div class="fgpm-btns">
        <button type="button" class="fgpm-btn is-primary" data-a="rematch">Rematch</button>
        <button type="button" class="fgpm-btn" data-a="menu">Back to menu</button>
      </div>
      <p class="fgpm-foot">
        <span class="fgpm-badge">&larr;</span><span class="fgpm-badge">&rarr;</span> Move
        <span class="fgpm-badge">Enter</span> Select
      </p>
    </div>`;

  root.appendChild(style);
  root.appendChild(el);

  const rematch = el.querySelector('[data-a="rematch"]') as HTMLButtonElement;
  const menu = el.querySelector('[data-a="menu"]') as HTMLButtonElement;
  rematch.addEventListener('click', h.onRematch);
  menu.addEventListener('click', h.onMenu);

  // Escape goes to the MENU, not to Rematch: Escape is "get me out of here"
  // everywhere else in the game, and silently restarting a match because
  // someone tapped it would be the opposite of that.
  const nav: NavOwner = createNavOwner({
    getNavElements: () => [rematch, menu],
    onBack: h.onMenu,
  });
  nav.setSelectedIndex(0);

  return {
    dispose: () => {
      nav.dispose();
      el.remove();
      style.remove();
    },
  };
}

const CSS = `
.fgpm {
  position: absolute; inset: 0; z-index: 40;
  display: grid; place-items: center;
  font-family: var(--fa-ui, 'Inter', system-ui, sans-serif);
  color: var(--fa-text, #f2f6fa);
  user-select: none;
  animation: fgpm-fade 320ms cubic-bezier(0.22,0.61,0.36,1) both;
}
@keyframes fgpm-fade { from { opacity: 0 } to { opacity: 1 } }

/* Darkening lives in the gradient, NOT in backdrop-filter's brightness().
   backdrop-filter is silently dropped under software rasterisation, some GL
   stacks and every headless capture — the pause overlay shipped that way once
   and sat on a fully lit pitch with unreadable text. The blur is a bonus; the
   contrast is guaranteed. */
.fgpm-veil {
  position: absolute; inset: 0;
  backdrop-filter: blur(20px) saturate(0.72);
  -webkit-backdrop-filter: blur(20px) saturate(0.72);
  background:
    radial-gradient(56% 44% at 50% 34%, rgba(255,226,168,0.07) 0%, rgba(255,226,168,0) 70%),
    radial-gradient(124% 104% at 50% 44%, rgba(4,7,12,0.9) 30%, rgba(4,7,12,0.98) 100%);
}

.fgpm-grain {
  position: absolute; inset: -50%;
  pointer-events: none; opacity: 0.3; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.42'/%3E%3C/svg%3E");
  animation: fgpm-grain 900ms steps(1) infinite;
}
@keyframes fgpm-grain {
  0%{transform:translate3d(0,0,0)} 25%{transform:translate3d(1%,-1.8%,0)}
  50%{transform:translate3d(-2%,1.2%,0)} 75%{transform:translate3d(2%,-0.8%,0)}
}

.fgpm * { box-sizing: border-box; }
.fgpm-card {
  position: relative;
  width: min(720px, calc(100vw - 48px));
  display: flex; flex-direction: column; align-items: center;
  text-align: center;
}

/* Same gold tick the menu and the pause overlay open with. */
.fgpm-kicker {
  position: relative; margin: 0 0 22px; padding-top: 18px;
  font-size: 12px; font-weight: 700; letter-spacing: 0.28em; text-transform: uppercase;
  color: var(--fa-accent, #e8c56a);
  animation: fgpm-rise 500ms cubic-bezier(0.16,0.84,0.44,1) 60ms both;
}
.fgpm-kicker::before {
  content: ''; position: absolute; top: 0; left: 50%; transform: translateX(-50%);
  width: 78px; height: 3px; background: var(--fa-accent, #e8c56a);
}

/* ── Scoreline ───────────────────────────────────────────────────────────
   The kit colour is a BAR beside the name, never a fill behind it: deriveKit
   can produce a pale yellow or a white kit, and white 800-weight text on that
   is unreadable. */
.fgpm-score {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr);
  align-items: center;
  gap: 24px;
  width: 100%;
  animation: fgpm-rise 560ms cubic-bezier(0.16,0.84,0.44,1) 140ms both;
}
.fgpm-team { display: flex; align-items: center; gap: 12px; min-width: 0; }
.fgpm-team-home { justify-content: flex-end; }
.fgpm-team-away { justify-content: flex-start; }
.fgpm-name {
  font-family: var(--fa-display, 'Barlow Condensed', 'Arial Narrow', sans-serif);
  font-size: clamp(20px, 2.4vw, 30px); font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fgpm-kit {
  flex: none; width: 6px; height: 30px; border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.45), 0 2px 10px rgba(0,0,0,0.5);
}
.fgpm-goals {
  display: flex; align-items: baseline; gap: 16px;
  font-family: var(--fa-display, 'Barlow Condensed', sans-serif);
  font-size: clamp(56px, 7vw, 92px); font-weight: 700; line-height: 1;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 6px 34px rgba(0,0,0,0.6);
}
.fgpm-goals em { font-style: normal; font-size: 0.42em; color: var(--fa-steel-dim, #5d7086); }

.fgpm-verdict {
  margin: 10px 0 0;
  font-size: 13px; font-weight: 700; letter-spacing: 0.24em; text-transform: uppercase;
  color: var(--fa-steel, #8fa6c0);
  animation: fgpm-rise 520ms cubic-bezier(0.16,0.84,0.44,1) 260ms both;
}
.fgpm-verdict.is-win  { color: var(--fa-accent, #e8c56a); }
.fgpm-verdict.is-loss { color: #e97474; }

/* ── Stats ─────────────────────────────────────────────────────────────── */
.fgpm-stats { display: flex; margin-top: 28px; }
.fgpm-stat {
  padding: 0 30px;
  border-left: 1px solid var(--fa-hairline, rgba(255,255,255,0.08));
  animation: fgpm-rise 500ms cubic-bezier(0.16,0.84,0.44,1) both;
  animation-delay: calc(340ms + var(--i, 0) * 70ms);
}
.fgpm-stat:first-child { border-left: 0; }
.fgpm-stat b {
  display: block;
  font-family: var(--fa-display, 'Barlow Condensed', sans-serif);
  font-size: 30px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums;
}
.fgpm-stat i {
  display: block; margin-top: 5px; font-style: normal;
  font-size: 10px; font-weight: 600; letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--fa-steel-dim, #5d7086);
}

/* ── Praise lines ──────────────────────────────────────────────────────── */
.fgpm-lines { display: flex; flex-direction: column; gap: 8px; margin-top: 30px; width: 100%; }
.fgpm-line {
  padding: 9px 22px; border-radius: var(--fa-r-md, 6px);
  font-size: 15px; font-weight: 700; letter-spacing: 0.05em;
  color: var(--fa-text, #f2f6fa);
  background: linear-gradient(90deg,
    rgba(232,197,106,0) 0%, rgba(232,197,106,0.1) 20%,
    rgba(232,197,106,0.1) 80%, rgba(232,197,106,0) 100%);
  animation: fgpm-rise 480ms cubic-bezier(0.16,0.84,0.44,1) both;
  animation-delay: calc(520ms + var(--i, 0) * 90ms);
}
/* A record is the only line that gets the gold. If everything is highlighted,
   nothing is. */
.fgpm-line.is-record {
  font-size: 17px;
  color: var(--fa-accent-hi, #f0d488);
  background: linear-gradient(90deg,
    rgba(232,197,106,0) 0%, rgba(232,197,106,0.2) 20%,
    rgba(232,197,106,0.2) 80%, rgba(232,197,106,0) 100%);
  text-shadow: 0 0 22px rgba(232,197,106,0.35);
}

/* ── Actions ───────────────────────────────────────────────────────────── */
.fgpm-btns {
  display: flex; gap: 12px; margin-top: 34px;
  animation: fgpm-rise 520ms cubic-bezier(0.16,0.84,0.44,1) 760ms both;
}
.fgpm-btn {
  height: 50px; padding-inline: 30px;
  border: 1px solid var(--fa-hairline-hi, rgba(255,255,255,0.16));
  border-radius: var(--fa-r-md, 6px);
  background: rgba(10,16,24,0.6);
  backdrop-filter: blur(8px);
  color: var(--fa-text, #f2f6fa);
  font-family: var(--fa-ui, 'Inter', system-ui, sans-serif);
  font-size: 14px; font-weight: 700; letter-spacing: 0.04em; cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, transform 160ms ease, filter 160ms ease;
}
.fgpm-btn:hover, .fgpm-btn:focus-visible {
  outline: none;
  border-color: var(--fa-accent-line, rgba(232,197,106,0.5));
  background: rgba(18,29,44,0.75);
  transform: translateY(-1px);
}
.fgpm-btn.is-primary {
  border: 0;
  background: linear-gradient(180deg, var(--fa-accent-hi, #f0d488), var(--fa-accent-lo, #d9ae4c));
  color: var(--fa-on-accent, #150f02);
  box-shadow: 0 10px 26px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4);
}
.fgpm-btn.is-primary:hover, .fgpm-btn.is-primary:focus-visible { filter: brightness(1.06); }

.fgpm-foot {
  display: flex; align-items: center; gap: 8px; margin: 20px 0 0;
  font-size: 12px; color: var(--fa-steel-dim, #5d7086);
  animation: fgpm-rise 480ms cubic-bezier(0.16,0.84,0.44,1) 880ms both;
}
.fgpm-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 20px; padding: 0 6px;
  border: 1px solid var(--fa-hairline-hi, rgba(255,255,255,0.16));
  border-radius: 4px; font-size: 11px; font-weight: 700;
  color: var(--fa-steel, #8fa6c0);
}
.fgpm-foot .fgpm-badge + .fgpm-badge { margin-left: -4px; }

@keyframes fgpm-rise {
  from { opacity: 0; transform: translate3d(0, 16px, 0); }
  to   { opacity: 1; transform: none; }
}

@media (max-height: 720px) {
  .fgpm-goals { font-size: clamp(40px, 5vw, 60px); }
  .fgpm-stats { margin-top: 18px; }
  .fgpm-lines { margin-top: 18px; gap: 6px; }
  .fgpm-btns { margin-top: 22px; }
  .fgpm-foot { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .fgpm, .fgpm-kicker, .fgpm-score, .fgpm-verdict,
  .fgpm-stat, .fgpm-line, .fgpm-btns, .fgpm-foot, .fgpm-grain {
    animation-duration: 0.001ms !important;
    animation-delay: 0ms !important;
    animation-iteration-count: 1 !important;
  }
}
`;
